import Sheet from 'components/utils/Sheet'
import React, { useCallback, useContext, useRef } from 'react'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import { useAppHeaderObserver } from 'hooks/useAppLayoutObservers'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { rem } from 'polished'
import MerchandiseCategoryMegaMenu from '../MerchandiseMegaMenu/MerchandiseCategoryMenu'
import MerchandiseBrandsMegaMenu from '../MerchandiseMegaMenu/MerchandiseBrandsMenu'
import MerchandiseTravelEssentialsMegaMenu from '../MerchandiseMegaMenu/MerchandiseTravelEssentialsMenu'
import { MerchandiseNavBarButton, MerchandiseNavBarFilterChip } from './MerchandiseNavBarButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ModalContext from 'contexts/ModalContext'
import MerchandiseCategoryDrawer from '../MerchandiseMegaMenu/MerchandiseCategoryDrawer'
import MerchandiseBrandsDrawer from '../MerchandiseMegaMenu/MerchandiseBrandsDrawer'
import MerchandiseTravelEssentialsDrawer from '../MerchandiseMegaMenu/MerchandiseTravelEssentialsDrawer'
import { useTrackedMenuOpen, trackMegaMenuOpen, trackMegaMenuLinkClick, MegaMenuSection } from 'analytics/snowplow/events/megaMenuEvents'

const Bar = styled(Sheet)`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickyHeader};
  padding: 0;

  &.disable-sticky {
    position: relative;
  }

  ${mediaQueryUp.desktop} {
    padding: 0 ${rem(20)};
  }
`

const ButtonsContainer = styled.div`
  position: relative;
  display: flex;
  gap: ${rem(4)};
`

const ChipsWrapper = styled.div`
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: ${rem(48)};
    background: linear-gradient(to left, ${props => props.theme.palette.neutral.default.eight}, transparent);
    pointer-events: none;
  }
`

const ChipsContainer = styled.div`
  display: flex;
  gap: ${rem(8)};
  padding: ${rem(12)};
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`

interface DesktopMenuConfig {
  label: string
  section: MegaMenuSection
  Menu: React.ComponentType<{ onClose: () => void }>
  alignDropdownWithButton?: boolean
}

const DESKTOP_MENUS: Array<DesktopMenuConfig> = [
  { label: 'Shop by Category', section: 'category', Menu: MerchandiseCategoryMegaMenu },
  { label: 'Shop by Brand', section: 'brand', Menu: MerchandiseBrandsMegaMenu },
  { label: 'Travel Essentials', section: 'travel_essential', Menu: MerchandiseTravelEssentialsMegaMenu, alignDropdownWithButton: true },
]

interface DirectLinkConfig {
  label: string
  section: MegaMenuSection
  to: string
}

interface MobileDrawerConfig {
  label: string
  section: MegaMenuSection
  Drawer: React.ComponentType
}

const DIRECT_LINKS: Array<DirectLinkConfig> = [
  { label: 'Free Shipping', section: 'free_shipping', to: '/search/shop?freeShipping=true' },
]

const MOBILE_DRAWERS: Array<MobileDrawerConfig> = [
  { label: 'Categories', section: 'category', Drawer: MerchandiseCategoryDrawer },
  { label: 'Brands', section: 'brand', Drawer: MerchandiseBrandsDrawer },
  { label: 'Travel Essentials', section: 'travel_essential', Drawer: MerchandiseTravelEssentialsDrawer },
]

function TrackedMenuButton({ label, section, Menu, alignDropdownWithButton }: DesktopMenuConfig) {
  const onOpenChange = useTrackedMenuOpen(section)
  return (
    <MerchandiseNavBarButton
      label={label}
      onOpenChange={onOpenChange}
      alignDropdownWithButton={alignDropdownWithButton}
    >
      {(menuOpen, onClose) => menuOpen && <Menu onClose={onClose} />}
    </MerchandiseNavBarButton>
  )
}

function TrackedLinkButton({ label, section, to }: DirectLinkConfig) {
  const handleClick = useCallback(() => trackMegaMenuLinkClick(section), [section])
  return (
    <MerchandiseNavBarButton
      label={label}
      to={to}
      onClick={handleClick}
      showChevron={false}
    />
  )
}

function TrackedLinkChip({ label, section, to }: DirectLinkConfig) {
  const handleClick = useCallback(() => trackMegaMenuLinkClick(section), [section])
  return (
    <MerchandiseNavBarFilterChip
      label={label}
      to={to}
      onClick={handleClick}
    />
  )
}

function TrackedDrawerChip({ label, section, Drawer }: MobileDrawerConfig) {
  const showModal = useContext(ModalContext)
  const handleClick = useCallback(() => {
    trackMegaMenuOpen(section)
    showModal(<Drawer />)
  }, [section, showModal, Drawer])

  return (
    <MerchandiseNavBarFilterChip
      label={label}
      showChevron
      onClick={handleClick}
    />
  )
}

function MerchandiseNavBar() {
  const barRef = useRef<HTMLDivElement>(null)
  useAppHeaderObserver(barRef, 'sticky')

  return <Bar background="neutral-eight" ref={barRef}>
    <CSSBreakpoint min="desktop">
      <ButtonsContainer>
        {DESKTOP_MENUS.map((config) => (
          <TrackedMenuButton key={config.section} {...config} />
        ))}
        {DIRECT_LINKS.map((config) => (
          <TrackedLinkButton key={config.section} {...config} />
        ))}
      </ButtonsContainer>
    </CSSBreakpoint>

    <CSSBreakpoint max="tablet">
      <ChipsWrapper>
        <ChipsContainer>
          {MOBILE_DRAWERS.map((config) => (
            <TrackedDrawerChip key={config.section} {...config} />
          ))}
          {DIRECT_LINKS.map((config) => (
            <TrackedLinkChip key={config.section} {...config} />
          ))}
        </ChipsContainer>
      </ChipsWrapper>
    </CSSBreakpoint>
  </Bar>
}

export default MerchandiseNavBar
